import React, { useState } from 'react';
import { Plus, Trash2, KanbanSquare, GanttChartSquare, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { capCreateTask, capUpdateTask, capDeleteTask, capUpdateProject } from '../../lib/capstoneApi';
import { PHASE_STATUS_COLORS, StatusBadge, fmtDate, inputCls, btnAmber, btnGhost } from './shared';
import GuidePanel from './GuidePanel';

const COLS = ['To Do', 'Doing', 'Review', 'Done'];
const PRIORITY_COLORS = { High: 'text-rose-300', Medium: 'text-amber-300', Low: 'text-white/45' };
const PHASE_STATUSES = ['Not Started', 'In Progress', 'Blocked', 'Done'];

export default function TasksTab({ project, tasks, reloadTasks, reloadProject }) {
  const [view, setView] = useState('kanban');
  const [phaseFilter, setPhaseFilter] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ title: '', owner: '', phase_id: '', priority: 'Medium', due_date: '', origin: 'self' });
  const [dragId, setDragId] = useState(null);

  const phases = project.milestones || [];
  const phaseName = (pid) => phases.find((m) => m.id === pid)?.name || '—';
  const filtered = phaseFilter ? tasks.filter((t) => t.phase_id === phaseFilter) : tasks;
  const members = project.members || [];
  const atRisk = project.at_risk_milestones || [];

  const addTask = async (payload) => {
    try {
      await capCreateTask(project.id, payload);
      reloadTasks();
      toast.success('Task added');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not add task'); }
  };

  const moveTask = async (taskId, status) => {
    try { await capUpdateTask(taskId, { status }); reloadTasks(); }
    catch { toast.error('Could not move task'); }
  };

  const setPhaseStatus = async (phaseId, status) => {
    const next = phases.map((m) => (m.id === phaseId ? { ...m, status } : m));
    try { await capUpdateProject(project.id, { milestones: next }); reloadProject(); toast.success('Phase updated'); }
    catch { toast.error('Could not update phase'); }
  };

  return (
    <div data-testid="cap-tasks-tab">
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="flex rounded-lg border border-white/12 overflow-hidden">
          <button onClick={() => setView('kanban')} data-testid="cap-view-kanban"
            className={`px-3 py-1.5 text-[11.5px] font-semibold flex items-center gap-1.5 ${view === 'kanban' ? 'bg-amber-300 text-slate-900' : 'text-white/60 hover:text-white'}`}>
            <KanbanSquare className="w-3.5 h-3.5" /> Kanban
          </button>
          <button onClick={() => setView('timeline')} data-testid="cap-view-timeline"
            className={`px-3 py-1.5 text-[11.5px] font-semibold flex items-center gap-1.5 ${view === 'timeline' ? 'bg-amber-300 text-slate-900' : 'text-white/60 hover:text-white'}`}>
            <GanttChartSquare className="w-3.5 h-3.5" /> Timeline
          </button>
        </div>
        <select className={`${inputCls} !w-auto`} value={phaseFilter} onChange={(e) => setPhaseFilter(e.target.value)} data-testid="cap-phase-filter">
          <option value="">All phases</option>
          {phases.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
        <button className={btnAmber} onClick={() => setShowAdd((v) => !v)} data-testid="cap-add-task-btn"><Plus className="w-4 h-4" /> Add task</button>
      </div>

      <GuidePanel
        topic="tasks"
        projectId={project.id}
        label="Let Guide suggest starter tasks for a phase"
        onUse={(text, origin, s) => addTask({ title: s.title, description: text, phase_id: phaseFilter || phases.find((m) => m.status === 'In Progress')?.id || phases[0]?.id, origin })}
      />

      {showAdd && (
        <div className="rounded-xl border border-white/12 bg-slate-900/60 p-4 my-4 grid sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="cap-task-form">
          <input className={inputCls} placeholder="Task title *" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="cap-task-title-input" />
          <select className={inputCls} value={form.phase_id} onChange={(e) => setForm({ ...form, phase_id: e.target.value })}>
            <option value="">Phase…</option>
            {phases.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
          <select className={inputCls} value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })}>
            <option value="">Owner…</option>
            {members.map((m, i) => <option key={i} value={m.name || m.email}>{m.name || m.email}</option>)}
          </select>
          <select className={inputCls} value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
            <option>High</option><option>Medium</option><option>Low</option>
          </select>
          <div className="flex gap-2">
            <input type="date" className={inputCls} value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
            <button className={btnAmber} disabled={!form.title.trim()} data-testid="cap-task-save-btn"
              onClick={async () => { await addTask(form); setForm({ title: '', owner: '', phase_id: '', priority: 'Medium', due_date: '', origin: 'self' }); setShowAdd(false); }}>
              Save
            </button>
          </div>
        </div>
      )}

      {view === 'kanban' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-5" data-testid="cap-kanban-board">
          {COLS.map((col) => (
            <div key={col}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => { if (dragId) { moveTask(dragId, col); setDragId(null); } }}
              className="rounded-xl border border-white/8 bg-white/[0.02] p-3 min-h-[220px]" data-testid={`cap-kanban-col-${col.replace(' ', '-').toLowerCase()}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-[10.5px] tracking-[0.18em] font-bold uppercase text-white/50">{col}</span>
                <span className="text-[10px] text-white/35">{filtered.filter((t) => t.status === col).length}</span>
              </div>
              {filtered.filter((t) => t.status === col).map((t) => (
                <div key={t.id} draggable onDragStart={() => setDragId(t.id)}
                  className="rounded-lg border border-white/10 bg-slate-900/70 p-3 mb-2.5 cursor-grab active:cursor-grabbing hover:border-amber-300/30 transition-colors"
                  data-testid={`cap-task-card-${t.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-[12.5px] text-white/85 font-medium leading-snug">{t.title}</div>
                    <button onClick={() => capDeleteTask(t.id).then(reloadTasks)} className="text-white/20 hover:text-rose-300 shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 mt-2 text-[10px] text-white/40">
                    <span className={`font-bold ${PRIORITY_COLORS[t.priority]}`}>{t.priority}</span>
                    {t.owner && <span>{t.owner}</span>}
                    {t.due_date && <span>{fmtDate(t.due_date)}</span>}
                    <span className="text-white/30">{phaseName(t.phase_id)}</span>
                    {t.origin !== 'self' && <span className="text-amber-200/50">✨</span>}
                  </div>
                  <div className="flex gap-1 mt-2">
                    {COLS.filter((c) => c !== col).map((c) => (
                      <button key={c} onClick={() => moveTask(t.id, c)} className="text-[9px] px-1.5 py-0.5 rounded bg-white/5 text-white/40 hover:text-white hover:bg-white/10">{c}</button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-5 rounded-2xl border border-white/10 bg-slate-900/50 p-5" data-testid="cap-timeline-view">
          {phases.map((m, i) => (
            <div key={m.id} className="flex flex-wrap items-center gap-3 py-3 border-b border-white/6 last:border-0">
              <span className="text-[11px] text-amber-300/70 font-bold w-6">{String(i + 1).padStart(2, '0')}</span>
              <div className="flex-1 min-w-[180px]">
                <div className="text-[13px] text-white/85 font-medium flex items-center gap-2">
                  {m.name}
                  {atRisk.includes(m.id) && <ShieldAlert className="w-3.5 h-3.5 text-amber-300" title="At risk — behind plan" />}
                </div>
                <div className="text-[10px] text-white/35 mt-0.5">
                  Planned {fmtDate(m.planned_start)} → {fmtDate(m.planned_end)}
                  {m.actual_start && <> · Actual {fmtDate(m.actual_start)} → {fmtDate(m.actual_end)}</>}
                  · {m.weight}% weight · {tasks.filter((t) => t.phase_id === m.id).length} tasks
                </div>
              </div>
              <select value={m.status} onChange={(e) => setPhaseStatus(m.id, e.target.value)}
                className={`text-[11px] rounded-md px-2 py-1 border bg-transparent focus:outline-none ${PHASE_STATUS_COLORS[m.status]} border-white/15`}
                data-testid={`cap-phase-status-${i}`}>
                {PHASE_STATUSES.map((s) => <option key={s} value={s} className="bg-slate-900 text-white">{s}</option>)}
              </select>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
